// DocViewerModal — in-app preview for library documents.
//
// Renders whichever preview the browser can handle natively:
//   * PDF          → <iframe> (Chrome/Edge/Firefox/Safari all ship
//                    a viewer; supports zoom + text search out of
//                    the box)
//   * image/*      → <img>
//   * text/plain   → <iframe> (browser renders text inline)
//   * anything else → fallback card with a download button
//
// The URL is a short-lived S3 presigned link fetched via
// storage.signedDownloadUrl().  Signing happens once per open —
// no periodic refresh; if the document stays open past the TTL
// the caller reopens.

import React from 'react';
import { X, Download, ExternalLink, FileText, AlertCircle } from 'lucide-react';
import { storage } from '../../storage.js';

const NAVY   = '#0D1B2A';
const ORANGE = '#E87722';
const TEXT   = '#F0F4F8';
const MUTED  = 'rgba(255,255,255,0.55)';
const BORDER = 'rgba(255,255,255,0.10)';

function isPdf(doc)   { return doc.mime_type === 'application/pdf' || (doc.filename || '').toLowerCase().endsWith('.pdf'); }
function isImage(doc) { return (doc.mime_type || '').startsWith('image/'); }
function isText(doc)  { return (doc.mime_type || '').startsWith('text/'); }

export default function DocViewerModal({ doc, bucket = 'proposal-library', onClose }) {
  const [url,    setUrl]    = React.useState(null);
  const [error,  setError]  = React.useState(null);

  React.useEffect(() => {
    if (!doc?.storage_path) { setError('This document has no attached file.'); return; }
    let cancelled = false;
    (async () => {
      const signed = await storage.signedDownloadUrl(bucket, doc.storage_path, 3600);
      if (cancelled) return;
      if (!signed) setError('Could not fetch a signed URL for this document.');
      else setUrl(signed);
    })();
    return () => { cancelled = true; };
  }, [doc?.id, doc?.storage_path, bucket]);

  const onEscape = React.useCallback((e) => {
    if (e.key === 'Escape') onClose?.();
  }, [onClose]);
  React.useEffect(() => {
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [onEscape]);

  const download = () => {
    if (!url) return;
    // Force download rather than viewing: fetch as blob + trigger a
    // synthetic <a download>.  Signed URLs don't carry a
    // Content-Disposition header from our /api layer.
    fetch(url).then(r => r.blob()).then(blob => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = doc.filename || doc.title || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    }).catch(() => {
      // Fallback: open in a new tab.
      window.open(url, '_blank', 'noopener');
    });
  };

  const openInTab = () => {
    if (url) window.open(url, '_blank', 'noopener');
  };

  const canInline = doc && (isPdf(doc) || isImage(doc) || isText(doc));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Document viewer"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(6, 12, 22, 0.72)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'stretch', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 1200, maxHeight: '92dvh',
          background: NAVY, color: TEXT,
          border: `1px solid ${BORDER}`, borderRadius: 12,
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          fontFamily: "'Montserrat',sans-serif",
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', borderBottom: `1px solid ${BORDER}`,
          background: 'rgba(0,0,0,0.15)', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <FileText size={16} color={ORANGE} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {doc?.title || doc?.filename || 'Document'}
              </div>
              <div style={{ fontSize: 10, color: MUTED, marginTop: 1 }}>
                {[doc?.document_type, doc?.mime_type, doc?.size_bytes ? `${(doc.size_bytes / 1024).toFixed(0)} KB` : null]
                  .filter(Boolean).join(' · ') || '—'}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button
              onClick={download}
              disabled={!url}
              title="Download"
              style={{
                background: 'transparent', border: `1px solid ${BORDER}`, color: TEXT,
                padding: '6px 10px', borderRadius: 6, cursor: url ? 'pointer' : 'default',
                fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                opacity: url ? 1 : 0.4,
                display: 'flex', alignItems: 'center', gap: 5,
              }}
            >
              <Download size={12} /> Download
            </button>
            <button
              onClick={openInTab}
              disabled={!url}
              title="Open in new tab"
              style={{
                background: 'transparent', border: `1px solid ${BORDER}`, color: TEXT,
                padding: '6px 10px', borderRadius: 6, cursor: url ? 'pointer' : 'default',
                fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                opacity: url ? 1 : 0.4,
                display: 'flex', alignItems: 'center', gap: 5,
              }}
            >
              <ExternalLink size={12} /> New tab
            </button>
            <button
              onClick={onClose}
              aria-label="Close viewer"
              style={{
                background: 'transparent', border: `1px solid ${BORDER}`, color: MUTED,
                width: 32, height: 32, borderRadius: 6, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, minHeight: 0, background: '#0a1420', display: 'flex' }}>
          {error && (
            <div style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 12,
              color: '#FCA5A5', padding: 24, textAlign: 'center',
            }}>
              <AlertCircle size={28} />
              <div style={{ fontSize: 13 }}>{error}</div>
            </div>
          )}

          {!error && !url && (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: MUTED, fontSize: 12,
            }}>Loading…</div>
          )}

          {!error && url && canInline && isPdf(doc) && (
            <iframe
              title={doc.title || 'PDF viewer'}
              src={url}
              style={{ flex: 1, border: 'none', background: '#111' }}
            />
          )}

          {!error && url && canInline && isImage(doc) && (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 20, overflow: 'auto',
            }}>
              <img
                src={url}
                alt={doc.title || doc.filename || 'Image'}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 4 }}
              />
            </div>
          )}

          {!error && url && canInline && isText(doc) && (
            <iframe
              title={doc.title || 'Text viewer'}
              src={url}
              style={{ flex: 1, border: 'none', background: '#fff' }}
            />
          )}

          {!error && url && !canInline && (
            <div style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 14,
              color: MUTED, padding: 24, textAlign: 'center',
            }}>
              <FileText size={36} color={MUTED} />
              <div style={{ fontSize: 13, color: TEXT, fontWeight: 700 }}>
                Preview not supported for this file type.
              </div>
              <div style={{ fontSize: 12, maxWidth: 420, lineHeight: 1.5 }}>
                {doc.mime_type
                  ? `The browser can't render ${doc.mime_type} inline. Download the file or open it in a new tab to view.`
                  : 'The browser can\'t determine how to render this file. Download it to view.'}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button
                  onClick={download}
                  style={{
                    background: ORANGE, color: '#fff', border: 'none',
                    padding: '9px 18px', borderRadius: 6,
                    fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 800,
                    letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <Download size={14} /> Download
                </button>
                <button
                  onClick={openInTab}
                  style={{
                    background: 'transparent', color: TEXT, border: `1px solid ${BORDER}`,
                    padding: '9px 14px', borderRadius: 6,
                    fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700,
                    letterSpacing: '0.04em', textTransform: 'uppercase', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <ExternalLink size={14} /> New tab
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
